import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { AuthService, EntityService, User } from '@app/core';
import {
  clearPlanExpiryPopupPending,
  hasPlanExpiryPopupPending,
  isPlanNearingExpiry,
  planExpiryPopupMessage,
  soonestActivePlanDays,
  subscriptionDatesFromCompany,
  subscriptionDatesFromOutlets
} from '@core/helpers/plan-expiry';
import { PmdModal } from '@app/shared/propeller';
import { Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

const OWNER_SELECTION_WAIT_MS = 2000;

@Component({
  selector: 'app-plan-expiry-popup',
  templateUrl: './plan-expiry-popup.component.html',
  styleUrls: ['./plan-expiry-popup.component.scss']
})
export class PlanExpiryPopupComponent implements AfterViewInit, OnDestroy {
  @ViewChild(PmdModal) planExpiryModal: PmdModal;
  message = '';

  private readonly destroy$ = new Subject<void>();
  private selectionSub: Subscription;
  private waitTimer: ReturnType<typeof setTimeout>;
  private settled = false;

  constructor(
    private readonly authService: AuthService,
    private readonly entityService: EntityService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.tryShow();
  }

  close(): void {
    this.planExpiryModal?.hide();
  }

  ngOnDestroy(): void {
    this.clearWait();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryShow(): void {
    if (!hasPlanExpiryPopupPending()) {
      return;
    }

    const user = this.authService.user;
    if (!user) {
      return;
    }

    if (!this.isEligible(user)) {
      this.finish([]);
      return;
    }

    if (this.isProfileSetupPending(user)) {
      return;
    }

    if (user.isOwner) {
      const resolved = this.resolveOwnerDates();
      if (!resolved.ready) {
        this.waitForOwnerSelection();
        return;
      }

      this.finish(resolved.dates);
      return;
    }

    this.finish(subscriptionDatesFromCompany(user.company));
  }

  private isEligible(user: User): boolean {
    if (user.isDemo || user.isAdmin || user.isLeadAdmin || user.isSuperAdmin) {
      return false;
    }

    return !!(user.isOwner || user.isOutlet || user.isUploader || user.isReviewer);
  }

  private isProfileSetupPending(user: User): boolean {
    if (!user.isOnboarded) {
      return true;
    }

    try {
      const stepData = localStorage.getItem('setupProfileData');
      const completedStep = localStorage.getItem('profileSetupCurrentPage');
      return !!(stepData && completedStep !== 'completed');
    } catch {
      return false;
    }
  }

  private resolveOwnerDates(): { ready: boolean; dates: unknown[] } {
    const selected = this.entityList(this.entityService.getSelectedEntities());
    const catalog = this.entityList(this.entityService.getEntities());
    const selectedDates = subscriptionDatesFromOutlets(selected, catalog);
    const hasRealSelection = selected.some(entity => this.isOutletId(entity?.id));

    if (hasRealSelection) {
      return {
        ready: true,
        dates: selectedDates.length ? selectedDates : subscriptionDatesFromCompany(this.authService.user?.company)
      };
    }

    if (!catalog.length) {
      return { ready: false, dates: [] };
    }

    const companyId = this.authService.user?.company?.id;
    const companyOutlets = catalog.filter(entity => entity?.parentId === companyId || entity?.parent === companyId);
    const fallbackOutlets = companyOutlets.length ? companyOutlets : catalog;
    const fallbackDates = subscriptionDatesFromOutlets(fallbackOutlets, catalog);

    return {
      ready: true,
      dates: fallbackDates.length ? fallbackDates : subscriptionDatesFromCompany(this.authService.user?.company)
    };
  }

  private waitForOwnerSelection(): void {
    this.selectionSub = this.entityService.selectedEntities$.pipe(
      filter(entities => this.entityList(entities).some(entity => this.isOutletId(entity?.id))),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.clearWait();
      this.finish(this.resolveOwnerDates().dates);
    });

    this.waitTimer = setTimeout(() => {
      this.selectionSub?.unsubscribe();
      this.selectionSub = null;
      const resolved = this.resolveOwnerDates();
      const dates = resolved.dates.length ? resolved.dates : subscriptionDatesFromCompany(this.authService.user?.company);
      this.finish(dates);
    }, OWNER_SELECTION_WAIT_MS);
  }

  private finish(dates: unknown[]): void {
    if (this.settled) {
      return;
    }

    this.settled = true;
    this.clearWait();
    clearPlanExpiryPopupPending();

    const days = soonestActivePlanDays(dates);
    if (!isPlanNearingExpiry(days)) {
      return;
    }

    this.message = planExpiryPopupMessage(days);
    this.changeDetectorRef.detectChanges();
    this.planExpiryModal?.show();
  }

  private entityList(value: unknown): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && typeof value === 'object') {
      return [value];
    }

    return [];
  }

  private isOutletId(id: unknown): boolean {
    return id != null && id !== '' && id !== '0' && id !== 0;
  }

  private clearWait(): void {
    if (this.waitTimer) {
      clearTimeout(this.waitTimer);
      this.waitTimer = null;
    }

    this.selectionSub?.unsubscribe();
    this.selectionSub = null;
  }
}
